import React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  Paper,
  ButtonBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";

// Placeholder components - we'll create simple versions
const ReusableDatePicker = ({ value, onChange, open, anchorEl, onClose, title }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <TextField
        type="date"
        value={value instanceof Date ? value.toISOString().split('T')[0] : value}
        onChange={(e) => onChange(new Date(e.target.value))}
        fullWidth
        label="Select Date"
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={() => onChange(value)}>OK</Button>
    </DialogActions>
  </Dialog>
);

import { SettingsIconWithDrawer } from './SettingsDrawer.jsx';

export function CalendarHeaderBar({
  selectedDate,
  viewMode = "Day",
  user,
  onPickMonth,
  onChangeViewMode,
  onLogout,
  onAddTask,
  onDateChange,

  // keep these for backward compatibility, but they won't be used for navigation now
  onPrev,
  onNext,
  onOpenSettings,
}) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const bg = "rgb(248, 248, 248)";
  const border = "0.5px solid rgba(0,0,0,0.1)";
  const textMuted = "rgb(133, 133, 133)";
  const textDark = "rgb(20, 20, 20)";

  // -----------------------------
  // Date helpers (mode-aware nav)
  // -----------------------------
  function clampToMidnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return clampToMidnight(x);
  }
  function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  function addMonthsClamped(d, deltaMonths) {
    // keep same day-of-month if possible; otherwise clamp to last day of target month
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();

    const target = new Date(year, month + deltaMonths, 1);
    const daysInTargetMonth = new Date(
      target.getFullYear(),
      target.getMonth() + 1,
      0
    ).getDate();

    target.setDate(Math.min(day, daysInTargetMonth));
    return clampToMidnight(target);
  }

  function navigateChevron(direction) {
    const sign = direction === "prev" ? -1 : 1;
    const current = clampToMidnight(selectedDate);

    let nextDate = current;

    if (viewMode === "day") {
      // Day view: jump by 1 week, same weekday
      nextDate = addDays(current, 7 * sign);
    } else if (viewMode === "week") {
      // Week view: jump by 1 week (week renderer should use the resulting date)
      nextDate = addDays(current, 7 * sign);
    } else if (viewMode === "multiday") {
      // Multi-Day view: jump by 4 days
      nextDate = addDays(current, 4 * sign);
    } else if (viewMode === "month") {
      // Month view: jump by 1 month
      nextDate = addMonthsClamped(current, 1 * sign);
    }

    // Update app state (drives week strip + fetching + views)
    onDateChange?.(nextDate);

    // Keep popover calendar month aligned
    setViewMonth(startOfMonth(nextDate));
  }

  // -----------------------------
  // Date Picker (popover)
  // -----------------------------
  const [datePickerAnchor, setDatePickerAnchor] = React.useState(null);
  const [viewMonth, setViewMonth] = React.useState(() => startOfMonth(selectedDate));
  const [isMonthYearView, setIsMonthYearView] = React.useState(false);
  const open = Boolean(datePickerAnchor);
  const id = open ? "date-picker-popover" : undefined;

  function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }
  function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
  }
  function sameDay(a, b) {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  const label = viewMonth.toLocaleString(undefined, { month: "long", year: "numeric" });
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const first = startOfMonth(viewMonth);
  const last = endOfMonth(viewMonth);
  const leadingBlanks = first.getDay();
  const totalDays = last.getDate();

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) {
    const d = new Date(first);
    d.setDate(1 - (leadingBlanks - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let day = 1; day <= totalDays; day++) {
    cells.push({ date: new Date(first.getFullYear(), first.getMonth(), day), inMonth: true });
  }
  while (cells.length < 42) {
    const d = new Date(last);
    d.setDate(last.getDate() + (cells.length - (leadingBlanks + totalDays) + 1));
    cells.push({ date: d, inMonth: false });
  }

  const selected = clampToMidnight(selectedDate);

  const handleDatePickerClick = (event) => {
    if (event.currentTarget && document.contains(event.currentTarget)) {
      setViewMonth(startOfMonth(selectedDate)); // open popover on the currently selected month
      setDatePickerAnchor(event.currentTarget);
    }
  };

  const handleDatePickerClose = () => setDatePickerAnchor(null);

  const handleDateSelect = (date) => {
    const d = clampToMidnight(date);
    onDateChange?.(d);
    onPickMonth?.({
      month: d.toLocaleString(undefined, { month: "long" }),
      year: d.getFullYear(),
    });
    setViewMonth(startOfMonth(d));
    handleDatePickerClose();
  };

  // Toggle between calendar grid and month/year view
  const handleViewToggle = () => {
    setIsMonthYearView(!isMonthYearView);
  };

  // Handle month selection in month/year view
  const handleMonthSelect = (monthIndex) => {
    const newMonth = new Date(viewMonth);
    newMonth.setMonth(monthIndex);
    setViewMonth(startOfMonth(newMonth));
  };

  // Handle year selection in month/year view
  const handleYearSelect = (year) => {
    const newYear = new Date(viewMonth);
    newYear.setFullYear(year);
    setViewMonth(startOfMonth(newYear));
  };

  const handleTodayClick = () => {
    const today = new Date();
    const d = clampToMidnight(today);
    onDateChange?.(d);
    onPickMonth?.({
      month: d.toLocaleString(undefined, { month: "long" }),
      year: d.getFullYear(),
    });
    setViewMonth(startOfMonth(d));
    handleDatePickerClose();
  };

  // Cleanup anchor on unmount
  React.useEffect(() => {
    return () => setDatePickerAnchor(null);
  }, []);

  return (
    <Box component="section" sx={{ width: "100%", background: bg, px: 2, py: 1.25 }}>
      {/* Top row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        {/* Left: Month Year + nav */}
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: "1 1 0%" }}>
          {/* Month/Year Date Picker Trigger */}
          <Box
            onClick={handleDatePickerClick}
            role="button"
            tabIndex={0}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              minWidth: 0,
              userSelect: "none",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: "24px", color: textDark, whiteSpace: "nowrap" }}>
              {selectedDate.toLocaleString(undefined, { month: "long" })}
            </Typography>

            <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: "24px", color: tokens.primary, whiteSpace: "nowrap" }}>
              {selectedDate.getFullYear()}
            </Typography>

            <ExpandMoreOutlinedIcon
              sx={{
                fontSize: 20,
                color: tokens.primary,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms ease",
              }}
            />
          </Box>

          {/* Date Picker Popover */}
          <Popover
            id={id}
            open={open}
            anchorEl={datePickerAnchor}
            onClose={handleDatePickerClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                borderRadius: 4,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                overflow: "hidden",
              },
            }}
          >
            <Paper sx={{ width: 320, p: 2, borderRadius: 4, background: "white" }}>
              {/* Header row */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, userSelect: "none" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: textDark }}>
                    {viewMonth.toLocaleString(undefined, { month: "long" })}
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: tokens.primary }}>
                    {viewMonth.getFullYear()}
                  </Typography>
                  <ButtonBase
                    onClick={handleViewToggle}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      p: 0.5, 
                      borderRadius: 1,
                      '&:hover': { backgroundColor: tokens.primarySubtle }
                    }}
                    aria-label="Toggle month year view"
                  >
                    {isMonthYearView ? (
                      <ExpandMoreOutlinedIcon sx={{ color: tokens.primary }} />
                    ) : (
                      <ChevronRightRoundedIcon sx={{ color: tokens.primary }} />
                    )}
                  </ButtonBase>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ButtonBase
                    onClick={handleTodayClick}
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: 999,
                      backgroundColor: tokens.primary,
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: tokens.primaryHover }
                    }}
                  >
                    Today
                  </ButtonBase>
                  <IconButton
                    size="small"
                    onClick={handleDatePickerClose}
                    sx={{ width: 30, height: 30, border: "1px solid rgba(0,0,0,0.06)", background: "white" }}
                    aria-label="Close date picker"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* DOW row - only show in calendar view */}
              {!isMonthYearView && (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.75 }}>
                  {daysOfWeek.map((d) => (
                    <Typography
                      key={d}
                      sx={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: textMuted, py: 0.5 }}
                    >
                      {d}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Conditional content: Calendar grid or Month/Year columns */}
              {!isMonthYearView ? (
                <>
                  {/* Calendar grid */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
                    {cells.map(({ date, inMonth }, idx) => {
                      const isSelected = sameDay(date, selected) && inMonth;
                      return (
                        <Box
                          key={`${date.toISOString()}-${idx}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDateSelect(date);
                          }}
                          sx={{
                            height: 36,
                            borderRadius: 999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: inMonth ? 1 : 0.35,
                            backgroundColor: isSelected ? `rgba(${primaryRgb}, 0.28)` : "transparent",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: isSelected ? `rgba(${primaryRgb}, 0.34)` : "rgba(0,0,0,0.04)",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: isSelected ? 800 : 700,
                              color: isSelected ? tokens.primary : textDark,
                              lineHeight: "16px",
                            }}
                          >
                            {date.getDate()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Month navigation */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5 }}>
                    <ButtonBase
                      onClick={() => setViewMonth((m) => addMonths(m, -1))}
                      sx={{ px: 1, py: 0.75, borderRadius: 2, color: textMuted }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Prev</Typography>
                    </ButtonBase>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: textMuted, alignSelf: "center" }}>
                      {label}
                    </Typography>
                    <ButtonBase
                      onClick={() => setViewMonth((m) => addMonths(m, 1))}
                      sx={{ px: 1, py: 0.75, borderRadius: 2, color: textMuted }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Next</Typography>
                    </ButtonBase>
                  </Box>
                </>
              ) : (
                /* Month/Year columns view */
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignItems: "center", py: 1 }}>
                  <ScrollingMonthColumn
                    selectedMonth={viewMonth.getMonth()}
                    onMonthSelect={handleMonthSelect}
                  />
                  <ScrollingYearColumn
                    selectedYear={viewMonth.getFullYear()}
                    onYearSelect={handleYearSelect}
                  />
                </Box>
              )}
            </Paper>
          </Popover>

          {/* Nav chevrons + divider + add */}
          <Box sx={{ display: "inline-flex", alignItems: "center", ml: 1.25 }}>
            <IconButton
              onClick={() => navigateChevron("prev")}
              size="small"
              sx={{ color: textMuted, p: 0.25 }}
              aria-label="Previous"
            >
              <ChevronLeftRoundedIcon fontSize="small" />
            </IconButton>

            <Box sx={{ width: "1px", height: 14, mx: 0.75, background: "rgba(0,0,0,0.15)" }} />

            <IconButton
              onClick={() => navigateChevron("next")}
              size="small"
              sx={{ color: textMuted, p: 0.25 }}
              aria-label="Next"
            >
              <ChevronRightRoundedIcon fontSize="small" />
            </IconButton>

            <Box sx={{ width: "8px" }} />

            <IconButton
              onClick={onAddTask}
              size="small"
              sx={{ color: tokens.primary, p: 0.25, "&:hover": { background: tokens.primarySubtle } }}
              aria-label="Add task"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Right: view segmented control + gear */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              background: "white",
              border,
              borderRadius: 999,
              height: 30,
              px: 0.5,
            }}
          >
            {["Day", "Multi-Day", "Week", "Month"].map((mode) => {
              const active = viewMode === mode.toLowerCase().replace('-', '');
              return (
                <Button
                  key={mode}
                  disableRipple
                  onClick={() => onChangeViewMode?.(mode.toLowerCase().replace('-', ''))}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    height: 24,
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: "16px",
                    color: active ? "white" : textMuted,
                    backgroundColor: active ? tokens.primary : "transparent",
                    "&:hover": { backgroundColor: active ? tokens.primary : "rgba(0,0,0,0.03)" },
                  }}
                >
                  {mode}
                </Button>
              );
            })}
          </Box>

          <SettingsIconWithDrawer
            userEmail={user?.email || "user@example.com"}
            userId={user?.uid}
            onResync={() => {}}
            onOpenPrivacy={() => {}}
            onLogout={onLogout}
            onDeleteAccount={() => {}}
            onResetApp={() => {}}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ------------------------------
// Scrolling Month Column
// ------------------------------
function ScrollingMonthColumn({ selectedMonth, onMonthSelect }) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const scrollRef = React.useRef(null);
  const itemHeight = 30;
  const visibleItems = 7;
  const containerHeight = itemHeight * visibleItems;

  const months = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => 
      new Date(2000, i, 1).toLocaleString(undefined, { month: "long" })
    ),
    []
  );

  // Scroll to selected month when component mounts or selection changes
  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = selectedMonth * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [selectedMonth, itemHeight]);

  const handleScroll = (event) => {
    const scrollElement = event.target;
    const scrollTop = scrollElement.scrollTop;
    const newMonthIndex = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(11, newMonthIndex));
    onMonthSelect(clampedIndex);
  };

  return (
    <Box sx={{ position: 'relative', height: containerHeight }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: containerHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Box sx={{ height: itemHeight * 3 }} />
        
        {months.map((month, idx) => {
          const isSel = idx === selectedMonth;
          const opacity = isSel ? 1 : 0.6;

          return (
            <Box
              key={month}
              sx={{
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: tokens.primarySubtle,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTop = idx * itemHeight;
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? tokens.primary : 'rgb(20, 20, 20)',
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {month}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ height: itemHeight * 3 }} />
      </Box>

      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: itemHeight,
          transform: 'translateY(-50%)',
          borderTop: `1px solid rgba(${primaryRgb}, 0.3)`,
          borderBottom: `1px solid rgba(${primaryRgb}, 0.3)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

// ------------------------------
// Scrolling Year Column
// ------------------------------
function ScrollingYearColumn({ selectedYear, onYearSelect }) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const scrollRef = React.useRef(null);
  const itemHeight = 30;
  const visibleItems = 7;
  const containerHeight = itemHeight * visibleItems;

  const currentYear = new Date().getFullYear();
  const years = React.useMemo(() => {
    const start = currentYear - 10; // 10 years ago
    const end = currentYear + 20; // 20 years ahead
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, []);

  const selectedYearIndex = Math.max(0, years.indexOf(selectedYear));

  // Scroll to selected year when component mounts or selection changes
  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = selectedYearIndex * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [selectedYearIndex, itemHeight]);

  const handleScroll = (event) => {
    const scrollElement = event.target;
    const scrollTop = scrollElement.scrollTop;
    const newYearIndex = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(years.length - 1, newYearIndex));
    onYearSelect(years[clampedIndex]);
  };

  return (
    <Box sx={{ position: 'relative', height: containerHeight }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: containerHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0,0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0,0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0,0.5)',
            },
          },
        }}
      >
        <Box sx={{ height: itemHeight * 3 }} />
        
        {years.map((year) => {
          const isSel = year === selectedYear;
          const opacity = isSel ? 1 : 0.6;

          return (
            <Box
              key={year}
              sx={{
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: tokens.primarySubtle,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => {
                const yearIndex = years.indexOf(year);
                if (scrollRef.current && yearIndex >= 0) {
                  scrollRef.current.scrollTop = yearIndex * itemHeight;
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? tokens.primary : 'rgb(20, 20, 20)',
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {year}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ height: itemHeight * 3 }} />
      </Box>

      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: itemHeight,
          transform: 'translateY(-50%)',
          borderTop: `1px solid rgba(${primaryRgb}, 0.3)`,
          borderBottom: `1px solid rgba(${primaryRgb}, 0.3)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

export default CalendarHeaderBar;
